import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { ArrowLeft, Copy, Check, RefreshCw, ExternalLink, Clock, AlertCircle, Search, ChevronRight, ShieldCheck, Sparkles, CreditCard, Wallet, Store, QrCode, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

const CATEGORY_META = {
  'QRIS':            { icon: QrCode,     accent: 'from-indigo-500 to-blue-500', chip: 'bg-indigo-50 text-indigo-700' },
  'E-Wallet':        { icon: Wallet,     accent: 'from-purple-500 to-pink-500', chip: 'bg-purple-50 text-purple-700' },
  'Virtual Account': { icon: CreditCard, accent: 'from-emerald-500 to-teal-500', chip: 'bg-emerald-50 text-emerald-700' },
  'Retail':          { icon: Store,      accent: 'from-amber-500 to-orange-500', chip: 'bg-amber-50 text-amber-700' },
  'Kartu Kredit':    { icon: CreditCard, accent: 'from-rose-500 to-red-500',     chip: 'bg-rose-50 text-rose-700' },
  'Lainnya':         { icon: Sparkles,   accent: 'from-slate-500 to-slate-600',  chip: 'bg-slate-100 text-slate-700' },
};

const categorize = (name = '') => {
  const n = name.toUpperCase();
  if (n.includes('QRIS') || n.includes('QR ')) return 'QRIS';
  if (n.includes('VA')) return 'Virtual Account';
  if (n.includes('RETAIL') || n.includes('ALFAMART') || n.includes('INDOMARET')) return 'Retail';
  if (n.includes('OVO') || n.includes('SHOPEEPAY') || n.includes('LINKAJA') || n.includes('DANA') || n.includes('GOPAY')) return 'E-Wallet';
  if (n.includes('CREDIT') || n.includes('KARTU') || n.includes('VISA') || n.includes('MASTERCARD')) return 'Kartu Kredit';
  return 'Lainnya';
};

const catOrder = ['QRIS', 'E-Wallet', 'Virtual Account', 'Retail', 'Kartu Kredit', 'Lainnya'];

const DuitkuPayment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = searchParams.get('invoice_id');

  const [loadingMethods, setLoadingMethods] = useState(true);
  const [methods, setMethods] = useState([]);
  const [baseAmount, setBaseAmount] = useState(0);
  const [search, setSearch] = useState('');
  const [creatingCode, setCreatingCode] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [paidInfo, setPaidInfo] = useState(null); // { paid_date, payment_method, amount }
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);

  const authHeader = () => {
    const t = localStorage.getItem('customer_token') || localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  // Kembali ke portal pelanggan yg benar (tenant-scoped kalau tersedia)
  const goBackToPortal = () => {
    try {
      const slug = localStorage.getItem('tenant_slug');
      window.location.href = slug ? `/p/${slug}/dashboard` : '/customer/dashboard';
    } catch {
      window.location.href = '/customer/dashboard';
    }
  };

  const fetchMethods = async () => {
    if (!invoiceId) return;
    try {
      setLoadingMethods(true);
      setErrorMsg('');
      const r = await axios.get(`${API_URL}/api/v2/payments/duitku/methods?invoice_id=${invoiceId}`, { headers: authHeader() });
      if (r.data?.success) {
        setMethods(r.data.methods || []);
        setBaseAmount(r.data.amount || 0);
      } else {
        setErrorMsg('Gagal memuat metode pembayaran');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Gagal memuat metode';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoadingMethods(false);
    }
  };

  useEffect(() => { fetchMethods(); /* eslint-disable-next-line */ }, [invoiceId]);

  // Auto-polling status pembayaran setelah instruksi terbuat (VA/QRIS)
  // Poll setiap 5 detik, max 15 menit (180 percobaan). Stop kalau paid atau kembali ke picker.
  useEffect(() => {
    // Kondisi mulai polling: ada `result` dan belum paid
    if (!result || !invoiceId || paidInfo) return;

    const checkStatus = async () => {
      try {
        pollCountRef.current += 1;
        const r = await axios.get(
          `${API_URL}/api/v2/customer/invoice/${invoiceId}/status`,
          { headers: authHeader() }
        );
        if (r.data?.success && r.data?.data?.is_paid) {
          setPaidInfo(r.data.data);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          toast.success('Pembayaran berhasil diterima!');
        } else if (pollCountRef.current > 180) {
          // Timeout 15 menit — stop polling
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch (e) {
        // Silent fail — jangan spam toast pas polling
      }
    };

    // Cek sekali di awal (immediate feedback kalau customer refresh page setelah bayar)
    checkStatus();
    pollRef.current = setInterval(checkStatus, 5000);

    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      pollCountRef.current = 0;
    };
    /* eslint-disable-next-line */
  }, [result, invoiceId, paidInfo]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q ? methods.filter(m => (m.name || '').toLowerCase().includes(q) || (m.code || '').toLowerCase().includes(q)) : methods;
    const out = {};
    filtered.forEach(m => {
      const cat = categorize(m.name);
      if (!out[cat]) out[cat] = [];
      out[cat].push(m);
    });
    return out;
  }, [methods, search]);

  const handleSelect = async (m) => {
    if (creatingCode) return;
    try {
      setCreatingCode(m.code);
      const r = await axios.post(`${API_URL}/api/v2/payments/duitku/inquiry`, {
        invoice_id: parseInt(invoiceId),
        payment_method: m.code
      }, { headers: authHeader() });
      if (r.data?.success && r.data?.data) {
        setResult({ ...r.data.data, methodName: m.name, methodImage: m.image, fee: m.fee });
        toast.success(`Instruksi ${m.name} berhasil dibuat`);
      } else {
        toast.error('Gagal membuat pembayaran');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal membuat pembayaran');
    } finally {
      setCreatingCode(null);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Disalin ke clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Gagal menyalin');
    }
  };

  // =========== VIEW: PAYMENT SUCCESS (after auto-poll detects payment) ===========
  if (paidInfo) {
    const paidAmt = paidInfo.amount || (result?.amount ? (result.amount + (result.fee || 0)) : baseAmount);
    let paidDateStr = '-';
    try {
      if (paidInfo.paid_date) {
        const d = new Date(paidInfo.paid_date);
        if (!isNaN(d)) {
          paidDateStr = d.toLocaleString('id-ID', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
        }
      }
    } catch { /* keep default */ }
    return (
      <div className="min-h-screen py-6 sm:py-10 px-4 scrollbar-hide flex items-center" style={{ background: '#F5F7FA' }} data-testid="payment-success-view">
        <div className="max-w-md mx-auto w-full">
          <div className="bg-white rounded-3xl shadow-[0_20px_50px_-12px_rgba(15,23,42,0.15)] overflow-hidden border border-emerald-100 dark:bg-slate-800 dark:border-emerald-800/50">
            {/* Success Hero */}
            <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white p-8 text-center overflow-hidden">
              <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute -left-10 bottom-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="relative">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full backdrop-blur-sm mb-4 animate-pulse">
                  <CheckCircle2 className="w-14 h-14 text-white" strokeWidth={2.2} data-testid="payment-success-icon" />
                </div>
                <p className="text-xs uppercase tracking-widest text-emerald-100/90 font-semibold">Pembayaran Berhasil</p>
                <p className="text-3xl sm:text-4xl font-bold mt-1 tracking-tight" data-testid="paid-amount">{fmtIDR(paidAmt)}</p>
                <p className="text-xs text-emerald-100/95 mt-3 font-mono">{paidInfo.invoice_number}</p>
              </div>
            </div>

            {/* Details */}
            <div className="p-6 space-y-3">
              {paidInfo.customer_name && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Nama Pelanggan</span>
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 text-right truncate ml-3" data-testid="paid-customer-name">{paidInfo.customer_name}</span>
                </div>
              )}
              {paidInfo.account_number && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-sm text-slate-500 dark:text-slate-400">ID Pelanggan</span>
                  <span className="text-sm font-mono font-semibold text-slate-800 dark:text-slate-200" data-testid="paid-account-number">{paidInfo.account_number}</span>
                </div>
              )}
              {paidInfo.service_plan_name && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Paket</span>
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 text-right truncate ml-3" data-testid="paid-service-plan">{paidInfo.service_plan_name}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
                <span className="text-sm text-slate-500 dark:text-slate-400">Tanggal Bayar</span>
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{paidDateStr}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-slate-500 dark:text-slate-400">Metode</span>
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{paidInfo.payment_method || result?.methodName || '-'}</span>
              </div>
              <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800/50">
                <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5 dark:text-emerald-400" />
                <p className="text-xs text-emerald-800 dark:text-emerald-200 leading-relaxed">
                  Terima kasih! Invoice Anda sudah lunas dan koneksi internet akan tetap aktif.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-slate-50/60 px-6 py-4 border-t border-slate-100 dark:bg-slate-900/30 dark:border-slate-700">
              <Button
                onClick={goBackToPortal}
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                data-testid="btn-back-to-portal"
              >
                Kembali ke Portal
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =========== VIEW: INSTRUCTION (after selecting a method) ===========
  if (result) {
    const isQris = !!result.qr_string;
    const isVa = !!result.va_number;
    const totalPay = (result.amount || baseAmount) + (result.fee || 0);
    return (
      <div className="min-h-screen py-6 sm:py-10 px-4 scrollbar-hide" style={{ background: '#F5F7FA' }}>
        <div className="max-w-md mx-auto">
          <button
            onClick={() => setResult(null)}
            className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-indigo-700 transition-colors group dark:text-slate-300"
            data-testid="back-to-picker"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            Pilih metode lain
          </button>

          <div className="bg-white rounded-3xl shadow-[0_20px_50px_-12px_rgba(15,23,42,0.15)] overflow-hidden border border-slate-100 dark:bg-slate-800 dark:border-slate-700">
            {/* Hero */}
            <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white p-6 sm:p-7 overflow-hidden">
              <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute -left-8 bottom-0 w-24 h-24 bg-white/10 rounded-full blur-xl" />
              <div className="relative">
                <p className="text-xs uppercase tracking-wider text-blue-100 opacity-90">Total Pembayaran</p>
                <p className="text-4xl sm:text-[42px] font-bold mt-1 tracking-tight" data-testid="total-amount">{fmtIDR(totalPay)}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-blue-100/95">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Batas waktu: {result.expiry_period || 1440} menit
                  </span>
                  <span className="font-mono opacity-80">{result.invoice_number}</span>
                </div>
              </div>
            </div>

            {/* Method Info */}
            <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-slate-100 dark:border-slate-700">
              {result.methodImage ? (
                <div className="w-14 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center p-1.5 dark:bg-slate-900 dark:border-slate-700">
                  <img src={result.methodImage} alt="" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-14 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold dark:text-slate-500">Metode Pembayaran</p>
                <p className="font-bold text-slate-800 truncate dark:text-slate-200">{result.methodName}</p>
              </div>
            </div>

            {/* QRIS */}
            {isQris && (
              <div className="p-6 text-center">
                <p className="text-sm text-slate-600 mb-4 dark:text-slate-300">Buka aplikasi e-wallet / m-banking, lalu <span className="font-semibold text-slate-800 dark:text-slate-200">scan QR</span> di bawah</p>
                <div className="inline-block p-5 bg-white rounded-2xl border-2 border-dashed border-indigo-200 shadow-inner dark:bg-slate-800">
                  <QRCodeSVG value={result.qr_string} size={240} level="M" includeMargin={false} />
                </div>
                <p className="text-xs text-slate-500 mt-4 flex items-center justify-center gap-1.5 dark:text-slate-400">
                  <QrCode className="w-3.5 h-3.5" /> QR berlaku {result.expiry_period || 1440} menit
                </p>
              </div>
            )}

            {/* VA */}
            {isVa && (
              <div className="p-6">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 dark:text-slate-500">Nomor Virtual Account</p>
                <div className="flex items-center justify-between gap-2 p-4 bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl dark:border-slate-700">
                  <span className="font-mono text-xl sm:text-2xl font-bold text-slate-800 tracking-wider select-all dark:text-slate-200" data-testid="va-number">
                    {result.va_number}
                  </span>
                  <button
                    onClick={() => copyText(result.va_number)}
                    className="flex-shrink-0 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 transition-colors flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-indigo-700 dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-indigo-900/30 dark:text-slate-300"
                    data-testid="copy-va"
                  >
                    {copied ? <><Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Tersalin</> : <><Copy className="w-3.5 h-3.5" /> Salin</>}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-3 leading-relaxed dark:text-slate-400">
                  Transfer <span className="font-semibold text-slate-700 dark:text-slate-200">{fmtIDR(totalPay)}</span> tepat ke nomor VA di atas.
                  Status invoice akan otomatis terupdate setelah pembayaran diterima.
                </p>

                {/* QR VA — shortcut copy via kamera/Google Lens */}
                <div className="mt-5 p-4 bg-white rounded-xl border border-dashed border-slate-300 dark:bg-slate-800/50 dark:border-slate-700" data-testid="va-qr-wrapper">
                  <div className="flex items-center gap-1.5 mb-3">
                    <QrCode className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
                      QR Nomor VA (Copy Cepat)
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 p-2.5 bg-white rounded-lg border border-slate-200 dark:bg-white">
                      <QRCodeSVG value={String(result.va_number)} size={112} level="M" includeMargin={false} data-testid="va-qr-code" />
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed dark:text-slate-400">
                      Scan dengan <span className="font-semibold text-slate-700 dark:text-slate-200">Google Lens / kamera HP</span> lalu paste ke menu <span className="font-semibold">Bayar → Virtual Account</span> di mBanking Anda. QR ini bukan QRIS, hanya berisi nomor VA untuk mempercepat input.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* E-Wallet redirect */}
            {!isQris && !isVa && result.payment_url && (
              <div className="p-6 text-center">
                <p className="text-sm text-slate-600 mb-4 dark:text-slate-300">Selesaikan pembayaran di aplikasi e-wallet Anda</p>
                <Button
                  onClick={() => window.open(result.payment_url, '_blank')}
                  className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold shadow-lg shadow-emerald-500/30"
                  data-testid="open-payment-url"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Buka Aplikasi Pembayaran
                </Button>
              </div>
            )}

            {/* Footer */}
            <div className="bg-slate-50/60 px-6 py-4 border-t border-slate-100 dark:border-slate-700">
              <p className="text-[11px] text-slate-500 flex items-center gap-1.5 dark:text-slate-400">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 dark:text-emerald-400" />
                Ref: <span className="font-mono text-slate-700 dark:text-slate-200">{result.reference}</span>
              </p>
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-indigo-600 dark:text-indigo-400" data-testid="auto-poll-hint">
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>Mengecek status pembayaran otomatis...</span>
              </div>
              <Button
                onClick={goBackToPortal}
                variant="outline"
                className="w-full mt-3 h-10"
                data-testid="close-instruction"
              >
                Kembali ke portal
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =========== VIEW: METHOD PICKER ===========
  return (
    <div className="min-h-screen py-6 sm:py-10 px-4 scrollbar-hide" style={{ background: '#F5F7FA' }}>
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-indigo-700 transition-colors group dark:text-slate-300"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Kembali
        </button>

        <div className="bg-white rounded-3xl shadow-[0_20px_50px_-12px_rgba(15,23,42,0.15)] border border-slate-100 overflow-hidden dark:bg-slate-800 dark:border-slate-700">
          {/* Hero */}
          <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white px-6 sm:px-8 py-6 sm:py-7 overflow-hidden">
            <div className="absolute -right-6 -top-6 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -left-10 -bottom-6 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-blue-100/90 font-semibold" data-testid="picker-title">Pilih Metode Pembayaran</p>
                <p className="text-3xl sm:text-4xl font-bold mt-1 tracking-tight">{fmtIDR(baseAmount)}</p>
                <p className="text-xs text-blue-100/90 mt-1.5">Nominal invoice yang harus dibayar</p>
              </div>
              <button
                onClick={fetchMethods}
                disabled={loadingMethods}
                className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors disabled:opacity-50"
                title="Muat ulang"
                data-testid="refresh-methods"
              >
                <RefreshCw className={`w-4 h-4 ${loadingMethods ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-6 sm:px-8 py-5 border-b border-slate-100 dark:border-slate-700">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari metode (BRI, QRIS, OVO, dll.)"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400 focus:bg-white focus:outline-none transition-all dark:border-slate-700 dark:bg-slate-900"
                data-testid="method-search"
              />
            </div>
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8">
            {loadingMethods ? (
              <div className="space-y-5">
                {[...Array(2)].map((_, i) => (
                  <div key={i}>
                    <div className="h-3 w-24 bg-slate-200 rounded animate-pulse mb-3 dark:bg-slate-700" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[...Array(4)].map((__, j) => (
                        <div key={j} className="h-16 bg-slate-100 rounded-xl animate-pulse dark:bg-slate-800" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : errorMsg ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-3 dark:bg-red-900/30">
                  <AlertCircle className="w-7 h-7 text-red-500" />
                </div>
                <p className="text-slate-800 font-semibold dark:text-slate-200">Gagal memuat metode</p>
                <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">{errorMsg}</p>
                <Button onClick={fetchMethods} className="mt-4" variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" /> Coba lagi
                </Button>
              </div>
            ) : methods.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
                <p>Belum ada metode pembayaran aktif</p>
              </div>
            ) : (
              <div className="space-y-7">
                {catOrder.map(cat => {
                  const items = grouped[cat];
                  if (!items || items.length === 0) return null;
                  const meta = CATEGORY_META[cat] || CATEGORY_META['Lainnya'];
                  const Icon = meta.icon;
                  return (
                    <div key={cat}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${meta.accent} flex items-center justify-center shadow-sm`}>
                          <Icon className="w-3.5 h-3.5 text-white" />
                        </div>
                        <h3 className="text-sm font-bold text-slate-800 tracking-tight dark:text-slate-200">{cat}</h3>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${meta.chip}`}>
                          {items.length} pilihan
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {items.map(m => {
                          const busy = creatingCode === m.code;
                          const total = baseAmount + (m.fee || 0);
                          return (
                            <button
                              key={m.code}
                              onClick={() => handleSelect(m)}
                              disabled={!!creatingCode}
                              className={`group relative flex items-center gap-3 p-3.5 rounded-xl text-left transition-all border
                                ${busy
                                  ? 'border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-500/10'
                                  : 'border-slate-200 bg-white hover:border-indigo-400 hover:shadow-lg hover:-translate-y-0.5'}
                                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none`}
                              data-testid={`method-${m.code}`}
                            >
                              {/* Logo box */}
                              <div className="w-14 h-10 flex-shrink-0 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center p-1.5 overflow-hidden group-hover:bg-white transition-colors relative dark:bg-slate-900 dark:border-slate-700">
                                {m.image ? (
                                  <>
                                    <img
                                      src={m.image}
                                      alt=""
                                      className="max-w-full max-h-full object-contain"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                    <div className="absolute inset-0 items-center justify-center hidden bg-slate-50 dark:bg-slate-900">
                                      <Icon className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                                    </div>
                                  </>
                                ) : (
                                  <Icon className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                                )}
                              </div>
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-sm font-semibold text-slate-800 truncate dark:text-slate-200">{m.name}</p>
                                  {m.requires_redirect && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 border border-blue-100 flex-shrink-0 dark:bg-blue-900/30 dark:text-blue-400">
                                      Redirect
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {m.fee > 0 ? (
                                    <>
                                      <span className="text-[11px] text-slate-500 dark:text-slate-400">Biaya {fmtIDR(m.fee)}</span>
                                      <span className="text-[11px] text-slate-400 dark:text-slate-500">·</span>
                                      <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">Total {fmtIDR(total)}</span>
                                    </>
                                  ) : (
                                    <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">Tanpa biaya layanan</span>
                                  )}
                                </div>
                              </div>
                              {/* Arrow / spinner */}
                              {busy ? (
                                <RefreshCw className="w-4 h-4 animate-spin text-indigo-600 flex-shrink-0 dark:text-indigo-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all flex-shrink-0 dark:text-slate-500" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Trust badge footer */}
          <div className="bg-slate-50/60 border-t border-slate-100 px-6 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 dark:border-slate-700">
            <p className="text-[11px] text-slate-500 flex items-center gap-1.5 dark:text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              Transaksi aman & terenkripsi
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              Powered by <span className="font-semibold text-slate-600 dark:text-slate-300">Duitku</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuitkuPayment;
